// Phase-B homepage discovery sections.
// Built on top of the reusable Carousel/Section/Skeleton primitives.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight } from 'lucide-react';
import { Section, Carousel, SkeletonCard } from './Primitives';
import { TRAVEL_THEMES as STATIC_THEMES, TESTIMONIALS } from './discoveryData';
import { api } from '../../lib/api';

// ── Theme tile ─────────────────────────────────────────────────────────
const ThemeTile = ({ theme }) => {
  // Fire-and-forget impression tracking once per mount per theme (CMS items only)
  useEffect(() => {
    if (!theme.id || String(theme.id).startsWith('static')) return;
    api.post(`/public/themes/${theme.id}/track?event=impression`).catch(() => {});
  }, [theme.id]);

  const onClick = () => {
    if (theme.id && !String(theme.id).startsWith('static')) {
      api.post(`/public/themes/${theme.id}/track?event=click`).catch(() => {});
    }
  };

  return (
    <Link
      to={theme.cta_url || theme.to || '/'}
      onClick={onClick}
      className="block relative h-44 sm:h-56 rounded-2xl overflow-hidden group focus:outline-none focus:ring-2 focus:ring-red-500"
      data-testid={`theme-${theme.id || theme.slug}`}
    >
      <img
        src={theme.image}
        alt={theme.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className={`absolute inset-0 bg-gradient-to-t ${theme.accent || 'from-red-500/30 to-red-900/80'}`} />
      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 text-white">
        <h3 className="text-base sm:text-lg font-bold drop-shadow-lg">{theme.title}</h3>
        <p className="text-[11px] sm:text-xs text-white/85 drop-shadow">{theme.subtitle || theme.sub}</p>
        <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
          {theme.cta_label || 'Explore'} <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
};

export const DiscoveryThemes = () => {
  const [themes, setThemes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const staticFallback = STATIC_THEMES.map((t) => ({
      ...t, subtitle: t.sub, cta_url: t.to, id: `static-${t.id}`,
    }));
    api.get('/public/themes', { params: { group: 'travel-themes', limit: 12 } })
      .then((r) => {
        if (!alive) return;
        const items = r.data?.items || [];
        setThemes(items.length > 0 ? items : staticFallback);
      })
      .catch(() => setThemes(staticFallback))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <Section
      eyebrow="Travel themes"
      title="Where will you go next?"
      sub="Curated picks across India — tap any theme to see live availability."
      className="py-8 sm:py-12"
    >
      {loading || !themes ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} h={176} w={220} />)}
        </div>
      ) : (
        <Carousel itemMinWidth={220} ariaLabel="travel themes">
          {themes.map((t) => <ThemeTile key={t.id || t.slug} theme={t} />)}
        </Carousel>
      )}
    </Section>
  );
};

// ── Testimonial card ───────────────────────────────────────────────────
const TestimonialCard = ({ t }) => (
  <article
    data-testid={`testimonial-${t.id}`}
    className="h-full flex flex-col gap-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 hover:shadow-lg transition-shadow"
  >
    <div className="flex items-center gap-1 text-amber-500" aria-label={`${t.rating} out of 5`}>
      {Array.from({ length: t.rating }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-current" />
      ))}
    </div>
    <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">“{t.text}”</p>
    <div className="mt-auto flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
      <img src={t.image} alt={t.name} loading="lazy" className="h-9 w-9 rounded-full object-cover" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{t.name}</p>
        <p className="text-[11px] text-gray-500 truncate">{t.location}</p>
      </div>
    </div>
  </article>
);

export const DiscoveryTestimonials = () => (
  <Section
    eyebrow="Real travellers"
    title="Loved by thousands across India"
    sub="What our customers say after booking with us."
    className="py-8 sm:py-12 bg-gray-50 dark:bg-gray-950/50 -mx-4 px-4 sm:-mx-0 sm:px-0"
  >
    <Carousel itemMinWidth={300} ariaLabel="customer testimonials">
      {TESTIMONIALS.map((t) => (
        <TestimonialCard key={t.id} t={t} />
      ))}
    </Carousel>
  </Section>
);
